import React, { useEffect, useState } from 'react';
import {
  Clock,
  AlertTriangle,
  FileText,
  Calendar,
  Coffee,
  X,
  ChevronRight,
  ExternalLink,
} from 'lucide-react';
import DashboardCard from './DashboardCard';
import type { ReportAttentionItem } from './types';

const iconMap = {
  clock: Clock,
  alert: AlertTriangle,
  file: FileText,
  calendar: Calendar,
  coffee: Coffee,
};

interface ReportsNeedingAttentionProps {
  items: ReportAttentionItem[];
  loading?: boolean;
}

const ReportsNeedingAttention: React.FC<ReportsNeedingAttentionProps> = ({ items, loading }) => {
  const [activeReport, setActiveReport] = useState<ReportAttentionItem | null>(null);

  useEffect(() => {
    if (!activeReport) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setActiveReport(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [activeReport]);

  const openInNewTab = (href: string) => {
    window.open(href, '_blank', 'noopener,noreferrer');
  };

  const openReport = (report: ReportAttentionItem) => {
    if (report.details && report.details.length > 0) {
      setActiveReport(report);
      return;
    }
    openInNewTab(report.href);
  };

  const goToDetail = (href: string) => {
    openInNewTab(href);
  };

  if (loading) {
    return (
      <DashboardCard title="Reports Needing Attention">
        <div className="px-5 pb-5 space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-14 bg-gray-50 rounded animate-pulse" />
          ))}
        </div>
      </DashboardCard>
    );
  }

  return (
    <>
      <DashboardCard title="Reports Needing Attention">
        <div className="divide-y divide-gray-100">
          {items.map((report) => {
            const Icon = iconMap[report.icon];
            return (
              <div
                key={report.id}
                className="flex items-center gap-3 px-5 py-3.5 hover:bg-gray-50/50"
              >
                <div className={`flex-shrink-0 p-2 rounded-lg ${report.iconBg}`}>
                  <Icon className={`h-4 w-4 ${report.iconColor}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">{report.title}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{report.description}</p>
                </div>
                <button
                  type="button"
                  onClick={() => openReport(report)}
                  className="flex-shrink-0 text-xs font-medium text-blue-600 border border-blue-200 rounded-md px-3 py-1.5 hover:bg-blue-50 hover:text-blue-700"
                >
                  View Report
                </button>
              </div>
            );
          })}
        </div>
      </DashboardCard>

      {activeReport && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
          role="dialog"
          aria-modal="true"
          aria-labelledby="report-attention-title"
          onClick={() => setActiveReport(null)}
        >
          <div
            className="bg-white rounded-lg shadow-xl border border-gray-200 w-full max-w-lg max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-3 border-b border-gray-100">
              <div className="min-w-0">
                <h3 id="report-attention-title" className="text-base font-semibold text-gray-900">
                  {activeReport.title}
                </h3>
                <p className="text-sm text-gray-500 mt-0.5">{activeReport.description}</p>
              </div>
              <button
                type="button"
                onClick={() => setActiveReport(null)}
                className="flex-shrink-0 p-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
              {(activeReport.details ?? []).map((row) => (
                <button
                  key={row.id}
                  type="button"
                  onClick={() => goToDetail(row.href)}
                  className="w-full flex items-center gap-3 px-5 py-3.5 text-left hover:bg-blue-50/60 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{row.primary}</p>
                    <p className="text-xs text-gray-500 mt-0.5 truncate">{row.secondary}</p>
                  </div>
                  <ChevronRight className="h-4 w-4 flex-shrink-0 text-gray-400" />
                </button>
              ))}
            </div>

            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-100">
              <button
                type="button"
                onClick={() => setActiveReport(null)}
                className="px-3 py-1.5 text-sm text-gray-600 border border-gray-200 rounded-md hover:bg-gray-50"
              >
                Close
              </button>
              {activeReport.hrefLabel ? (
                <button
                  type="button"
                  onClick={() => goToDetail(activeReport.href)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                >
                  {activeReport.hrefLabel}
                  <ExternalLink className="h-3.5 w-3.5" />
                </button>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ReportsNeedingAttention;
